import { useState } from "react";
import api, { formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import { Sparkle } from "@phosphor-icons/react";

/**
 * AI-assisted summary block for any calculator tab.
 *
 * Drop in below a calculator's result JSX:
 *   <CalcAiSummary calculator="emi" label="EMI" inputs={form} result={result} />
 *
 * - `calculator`  : short id ("emi", "rentbuy", "invest", "cashflow", ...) — backend has tailored prompts per id.
 * - `label`       : human-readable name shown in the panel header.
 * - `inputs`      : the calculator's input form state (sent to LLM for context).
 * - `result`      : the calculator's response object (must be truthy before user can request summary).
 * - `disabled`    : optional — pass true to show the panel but block the button (e.g. before any calc runs).
 */
export default function CalcAiSummary({ calculator, label, inputs, result, disabled = false }) {
  const [summary, setSummary] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const ready = !disabled && result && Object.keys(result || {}).length > 0;

  const generate = async () => {
    setBusy(true);
    setErr("");
    setSummary("");
    try {
      const { data } = await api.post("/calc/ai-summary", {
        calculator,
        label,
        inputs: inputs || {},
        result: result || {},
      });
      setSummary(data.summary || "");
    } catch (e) {
      const msg = formatApiErrorDetail(e.response?.data?.detail) || "AI summary unavailable";
      setErr(msg);
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  const copy = async () => {
    try {
      await navigator.clipboard?.writeText(summary);
      toast.success("Summary copied");
    } catch {
      toast.error("Copy failed");
    }
  };

  return (
    <div className="card-flat p-5 mt-6" data-testid={`calc-ai-summary-${calculator}`}>
      <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
        <div>
          <div className="eyebrow text-[10px] mb-0.5 text-[hsl(var(--secondary))] inline-flex items-center gap-1">
            <Sparkle size={11} weight="duotone" /> AI summary · {label}
          </div>
          <p className="text-xs text-muted-foreground">
            Get a 3-paragraph plain-English read of this calculation — verdict, key driver, and one concrete action.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {summary && (
            <button
              onClick={copy}
              className="text-xs px-3 py-2 border hairline hover:border-[hsl(var(--secondary))] hover:text-[hsl(var(--secondary))] transition"
              data-testid={`calc-ai-summary-copy-${calculator}`}
            >
              Copy
            </button>
          )}
          <button
            onClick={generate}
            disabled={busy || !ready}
            className="btn-primary px-4 py-2 text-sm inline-flex items-center gap-2 disabled:opacity-50"
            title={ready ? "Generate AI summary" : "Run the calculation first"}
            data-testid={`calc-ai-summary-generate-${calculator}`}
          >
            <Sparkle size={12} weight="bold" />
            {busy ? "Thinking…" : (summary ? "Regenerate" : "Generate")}
          </button>
        </div>
      </div>

      {err && !summary && (
        <p className="text-xs text-destructive" data-testid={`calc-ai-summary-error-${calculator}`}>{err}</p>
      )}
      {!ready && !summary && (
        <p className="text-xs text-muted-foreground italic" data-testid={`calc-ai-summary-empty-${calculator}`}>
          Run the calculator above first — the AI needs the result to summarise.
        </p>
      )}
      {summary && (
        <div className="space-y-3 text-sm leading-relaxed text-foreground/90 whitespace-pre-wrap" data-testid={`calc-ai-summary-text-${calculator}`}>
          {summary.split(/\n\s*\n/).map((p, i) => (
            <p key={`${calculator}-p${i}`} className="font-serif">{p.trim()}</p>
          ))}
        </div>
      )}
    </div>
  );
}
